import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShoppingCart, Save, Info, DoorOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PVCNotifyForm from "../components/PVCNotifyForm";
import { useCart } from "../context/CartContext";
import PVCEnclosureViewer from "../components/PVCEnclosureViewer";
import ARViewButton from "../components/ARViewButton";
import SpeciesWizard from "../components/SpeciesWizard";
import ShareConfigButton from "../components/ShareConfigButton";
import BuildQueueBadge from "../components/BuildQueueBadge";
import KlarnaBadge from "../components/KlarnaBadge";
import BundleUpsell from "../components/BundleUpsell";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PVC_COLORS = {
  white: { name: "White", hex: "#F2F2F2" },
  black: { name: "Black", hex: "#1F1F1F" },
};

const PVCConfiguratorPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  
  const [config, setConfig] = useState({
    width: 4,
    height: 3,
    depth: 2,
    pvc_color: "white",
    type: "pvc", // Mark as PVC enclosure
  });
  
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState(null);

  // Hydrate config from URL params on mount (Share-this-build deep links).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const hydration = {};
    const w = searchParams.get("w") || searchParams.get("width");
    const h = searchParams.get("h") || searchParams.get("height");
    const d = searchParams.get("d") || searchParams.get("depth");
    const pvc = searchParams.get("pvc") || searchParams.get("pvc_color");
    if (w) hydration.width = Math.max(2, Math.min(8, Number(w)));
    if (h) hydration.height = Math.max(2, Math.min(4, Number(h)));
    if (d) hydration.depth = Math.max(2, Math.min(4, Number(d)));
    if (pvc && PVC_COLORS[pvc]) hydration.pvc_color = pvc;
    if (Object.keys(hydration).length > 0) {
      setConfig((prev) => ({ ...prev, ...hydration }));
      toast.success("Loaded shared build configuration");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Determine door configuration
  const useDoubleDoors = config.width > 4 || config.height > 4 || config.depth > 4;
  
  // Calculate price when config changes
  useEffect(() => {
    const calculatePrice = async () => {
      try {
        const response = await axios.post(`${API}/calculate-pvc-price`, config);
        setPrice(response.data);
      } catch (error) {
        console.error("Error calculating price:", error);
      }
    };
    calculatePrice();
  }, [config]);
  
  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please login to add items to cart");
      navigate("/login");
      return;
    }
    
    setLoading(true);
    try {
      await addToCart({ ...config, price: price?.total }, 1);
      toast.success("PVC Enclosure added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveConfig = async () => {
    if (!user) {
      toast.error("Please login to save configurations");
      navigate("/login");
      return;
    }
    
    setSavingConfig(true);
    try {
      await axios.post(
        `${API}/configurations/save`,
        config,
        { params: { name: `PVC ${config.width}x${config.height}x${config.depth}` } }
      );
      toast.success("Configuration saved!");
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleWizardApply = (rec) => {
    setConfig((prev) => ({
      ...prev,
      width: rec.width,
      height: rec.height,
      depth: rec.depth,
    }));
    setSelectedSpeciesId(rec.speciesId || null);
    toast.success(`Applied recommended setup for ${rec.speciesName}`);
  };
  
  // AR config for PVC model
  const arConfig = {
    ...config,
    shape: 'pvc', // Special flag for PVC model generation
    stain_color: config.pvc_color, // Map to stain_color for compatibility
  };

  return (
    <div className="min-h-screen pt-16 bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-gradient-to-b from-blue-900/30 to-transparent py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <DoorOpen className="w-5 h-5 text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE]">
              PVC Enclosure Builder
            </h1>
          </div>
          <p className="text-[#F4F1DE]/60 ml-13">
            Lightweight, easy-clean PVC enclosures with hinged acrylic doors
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Out-of-stock notify banner — sits above the configurator so visitors see it first */}
        <div className="mb-8">
          <PVCNotifyForm variant="card" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 3D Viewer */}
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
            <div className="aspect-[4/3]">
              <PVCEnclosureViewer config={config} />
            </div>
          </div>
          
          {/* Controls */}
          <div 
            className="bg-[#121212] border border-white/10 rounded-lg p-6 overflow-y-auto"
            data-testid="pvc-configurator-controls"
          >
            {/* Build queue badge */}
            <BuildQueueBadge />

            {/* Species Wizard */}
            <div className="mb-3">
              <SpeciesWizard variant="pvc" onApply={handleWizardApply} />
            </div>
            <div className="mb-6">
              <ShareConfigButton variant="pvc" config={config} summary={`a PVC enclosure (${config.width}×${config.height}×${config.depth}ft)`} />
            </div>

            {/* Price Display - Always visible at top */}
            <div className="mb-6 p-4 bg-[#0A0A0A] border border-white/10 rounded-lg" data-testid="pvc-price-display">
              <p className="text-[#F4F1DE]/60 text-sm uppercase tracking-widest mb-2">Total Price</p>
              <p className="text-4xl font-bold font-['Space_Grotesk'] text-blue-400" data-testid="pvc-total-price">
                £{price?.total?.toFixed(2) || "0.00"}
              </p>
              <KlarnaBadge totalPrice={price?.total} />
              {price && (
                <div className="mt-4 space-y-1 text-sm text-[#F4F1DE]/50">
                  <div className="flex justify-between">
                    <span>PVC Panels</span>
                    <span>£{price.breakdown.pvc_panels?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Acrylic Windows</span>
                    <span>£{price.breakdown.acrylic_windows?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Hardware</span>
                    <span>£{price.breakdown.hardware?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Labor</span>
                    <span>£{price.breakdown.labor?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span>Subtotal</span>
                    <span>£{price.breakdown.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT (20%)</span>
                    <span>£{price.breakdown.vat?.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* What's Included */}
              <div className="mt-4 pt-4 border-t border-white/10" data-testid="pvc-whats-included">
                <p className="text-[10px] uppercase tracking-[0.22em] text-blue-400/80 font-semibold mb-2">
                  What's included in every build
                </p>
                <ul className="text-xs text-[#F4F1DE]/70 space-y-1.5 leading-relaxed">
                  <li className="flex gap-2"><span className="text-blue-400">•</span> 10mm structural PVC panels — fully waterproof & warp-free</li>
                  <li className="flex gap-2"><span className="text-blue-400">•</span> Clear acrylic front with stainless-steel hinges & handles</li>
                  <li className="flex gap-2"><span className="text-blue-400">•</span> Integrated top vent strip & pre-drilled cable entry points</li>
                  <li className="flex gap-2"><span className="text-blue-400">•</span> Easy-clean smooth interior — ideal for humid & tropical setups</li>
                  <li className="flex gap-2"><span className="text-blue-400">•</span> Hand-built to order in the UK — 7-10 working day lead time</li>
                </ul>
              </div>
            </div>

            {/* Bundle Upsell */}
            <BundleUpsell selectedSpeciesId={selectedSpeciesId} defaultKit="rainforest_arboreal" />

            {/* Dimensions */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-[#F4F1DE] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-500/20 rounded flex items-center justify-center text-xs text-blue-400">1</span>
                Dimensions
              </h2>
              
              <div className="space-y-6">
                {/* Width */}
                <div>
                  <div className="flex justify-between mb-2">
                    <Label className="text-[#F4F1DE]/80">Width</Label>
                    <span className="text-blue-400 font-mono">{config.width}ft</span>
                  </div>
                  <Slider
                    value={[config.width]}
                    onValueChange={([v]) => setConfig(prev => ({ ...prev, width: v }))}
                    min={2}
                    max={8}
                    step={0.5}
                    className="[&_[role=slider]]:bg-blue-500"
                  />
                  <div className="flex justify-between text-xs text-[#F4F1DE]/40 mt-1">
                    <span>2ft</span>
                    <span>8ft</span>
                  </div>
                </div>
                
                {/* Height */}
                <div>
                  <div className="flex justify-between mb-2">
                    <Label className="text-[#F4F1DE]/80">Height</Label>
                    <span className="text-blue-400 font-mono">{config.height}ft</span>
                  </div>
                  <Slider
                    value={[config.height]}
                    onValueChange={([v]) => setConfig(prev => ({ ...prev, height: v }))}
                    min={2}
                    max={4}
                    step={0.5}
                    className="[&_[role=slider]]:bg-blue-500"
                  />
                  <div className="flex justify-between text-xs text-[#F4F1DE]/40 mt-1">
                    <span>2ft</span>
                    <span>4ft</span>
                  </div>
                </div>
                
                {/* Depth */}
                <div>
                  <div className="flex justify-between mb-2">
                    <Label className="text-[#F4F1DE]/80">Depth</Label>
                    <span className="text-blue-400 font-mono">{config.depth}ft</span>
                  </div>
                  <Slider
                    value={[config.depth]}
                    onValueChange={([v]) => setConfig(prev => ({ ...prev, depth: v }))}
                    min={2}
                    max={4}
                    step={0.5}
                    className="[&_[role=slider]]:bg-blue-500"
                  />
                  <div className="flex justify-between text-xs text-[#F4F1DE]/40 mt-1">
                    <span>2ft</span>
                    <span>4ft</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* PVC Color */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-[#F4F1DE] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-500/20 rounded flex items-center justify-center text-xs text-blue-400">2</span>
                PVC Color
              </h2>
              
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(PVC_COLORS).map(([key, { name, hex }]) => (
                  <button
                    key={key}
                    onClick={() => setConfig(prev => ({ ...prev, pvc_color: key }))}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      config.pvc_color === key 
                        ? 'border-blue-500 bg-blue-500/10' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`pvc-color-${key}`}
                  >
                    <div 
                      className="w-full h-8 rounded mb-2 border border-white/20"
                      style={{ backgroundColor: hex }}
                    />
                    <span className="text-xs text-[#F4F1DE]/80">{name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Door Info */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-[#F4F1DE] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-500/20 rounded flex items-center justify-center text-xs text-blue-400">3</span>
                Door Configuration
              </h2>
              
              <div className={`p-4 rounded-lg border ${useDoubleDoors ? 'border-blue-500/30 bg-blue-500/10' : 'border-white/10 bg-white/5'}`}>
                <div className="flex items-center gap-3">
                  <DoorOpen className={`w-8 h-8 ${useDoubleDoors ? 'text-blue-400' : 'text-[#F4F1DE]/60'}`} />
                  <div>
                    <p className="text-[#F4F1DE] font-medium">
                      {useDoubleDoors ? 'Double Hinged Doors' : 'Single Hinged Door'}
                    </p>
                    <p className="text-[#F4F1DE]/50 text-sm">
                      {useDoubleDoors 
                        ? 'Two doors with center PVC divider strip' 
                        : 'One full-width door with left-side hinges'}
                    </p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-xs text-[#F4F1DE]/40">
                    <span className="text-blue-400">•</span> Clear perspex acrylic windows
                  </p>
                  <p className="text-xs text-[#F4F1DE]/40 mt-1">
                    <span className="text-blue-400">•</span> {useDoubleDoors ? '4 stainless steel hinges' : '3 stainless steel hinges'}
                  </p>
                  <p className="text-xs text-[#F4F1DE]/40 mt-1">
                    <span className="text-blue-400">•</span> {useDoubleDoors ? '2 handles' : '1 handle'}
                  </p>
                </div>
              </div>
              
              <p className="text-xs text-[#F4F1DE]/40 mt-3">
                * Enclosures larger than 4×4×4 ft automatically get double doors for easier access
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleAddToCart}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 font-bold uppercase tracking-widest"
                data-testid="pvc-add-to-cart-btn"
              >
                {loading ? (
                  <span className="spinner mr-2" />
                ) : (
                  <ShoppingCart className="w-5 h-5 mr-2" />
                )}
                Add to Cart
              </Button>
              
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handleSaveConfig}
                  disabled={savingConfig}
                  variant="outline"
                  className="border-white/20 hover:border-blue-500/50 text-[#F4F1DE] py-4"
                >
                  <Save className="w-5 h-5 mr-2" />
                  Save
                </Button>
                
                <ARViewButton config={{ ...arConfig, isPVC: true }} />
              </div>
            </div>
            
            {/* Info */}
            <div className="mt-6 p-4 bg-[#0A0A0A] border border-white/10 text-xs text-[#F4F1DE]/50">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 text-blue-400" />
                <p>
                  PVC enclosures feature lightweight, waterproof panels with clear acrylic windows. 
                  Easy to clean and maintain. All hardware is stainless steel.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PVCConfiguratorPage;
